// YNX — MEETINGS MODULE (Jitsi-powered, free, unlimited).
// Carl can create rooms, invite partners, launch the conference, control
// participants (mute everyone, kick, share link) — all from inside YNX.
import React, { useEffect, useState, useCallback } from 'react';
import {
  Plus, Trash2, X, Video, Copy, Share2, Play, Users,
  CalendarDays, ShieldCheck, Sparkles, Mic,
} from 'lucide-react';
import {
  listMeetings, createMeeting, deleteMeeting, markMeetingStarted,
  ensureMeetingIndexes,
} from '../../lib/meetings';
import { MediaCheck } from './MediaCheck';
import { JitsiRoom } from './JitsiRoom';
import './meetings.css';

const EMPTY_FORM = {
  title: '', description: '', scheduled_at: '', duration_min: 60,
  host_name: '', host_email: '',
  participants_raw: '',  // textarea: one "name <email>" per line
  require_password: false, password: '',
};

const parseParticipants = (raw) => {
  if (!raw) return [];
  return raw.split(/\r?\n/).map((line) => {
    const m = line.match(/^\s*([^<]+?)?\s*<([^>]+)>\s*$/);
    if (m) return { name: (m[1] || '').trim(), email: m[2].trim() };
    const justEmail = line.match(/^\s*([^\s@]+@[^\s@]+\.[A-Za-z]{2,})\s*$/);
    if (justEmail) return { name: '', email: justEmail[1] };
    return { name: line.trim(), email: '' };
  }).filter((p) => p.name || p.email);
};

export const MeetingsModule = ({ workspace, currentUser }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const [precheckMeeting, setPrecheckMeeting] = useState(null);  // meeting being prepared
  const [activeMeeting, setActiveMeeting] = useState(null);      // meeting currently live

  const refresh = useCallback(async () => {
    setLoading(true);
    try { setItems(await listMeetings()); }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    ensureMeetingIndexes();
    refresh();
  }, [refresh]);

  const openCreate = () => {
    setForm({
      ...EMPTY_FORM,
      host_name:  currentUser?.name  || currentUser?.email?.split('@')[0] || '',
      host_email: currentUser?.email || '',
    });
    setShowForm(true);
    setError('');
  };

  const submit = async () => {
    if (!form.title.trim()) { setError('Le titre est requis.'); return; }
    setBusy(true);
    setError('');
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim(),
        scheduled_at: form.scheduled_at,
        duration_min: Number(form.duration_min) || 60,
        host_name: form.host_name.trim(),
        host_email: form.host_email.trim(),
        participants: parseParticipants(form.participants_raw),
        require_password: !!form.require_password,
        password: form.password.trim(),
      };
      const created = await createMeeting(payload);
      setShowForm(false);
      setForm(EMPTY_FORM);
      await refresh();
      // Auto-open the pre-check for the freshly created meeting.
      setPrecheckMeeting(created);
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Supprimer cette réunion ?')) return;
    try { await deleteMeeting(id); await refresh(); }
    catch (e) { alert(e.message); }
  };

  const startMeeting = async (m) => {
    setPrecheckMeeting(m);
  };

  const launchFromPrecheck = async () => {
    if (!precheckMeeting) return;
    try { await markMeetingStarted(precheckMeeting.id); }
    catch (err) { console.warn('YNX meetings: markStarted failed (non-blocking)', err); }
    setActiveMeeting(precheckMeeting);
    setPrecheckMeeting(null);
  };

  const copyInvite = async (m) => {
    const body = `Vous êtes invité(e) à la réunion : ${m.title}\n`
      + (m.scheduled_at ? `Date : ${new Date(m.scheduled_at).toLocaleString('fr-FR')}\n` : '')
      + (m.description ? `\n${m.description}\n` : '')
      + `\nLien : ${m.join_url}\n`
      + (m.require_password && m.password ? `Mot de passe : ${m.password}\n` : '')
      + `\n— Envoyé via YNX`;
    try { await navigator.clipboard.writeText(body); }
    catch (err) { console.warn('YNX meetings: clipboard write blocked', err); }
  };

  return (
    <div className='biz-module meetings-module' data-testid='meetings-module'>
      <header className='biz-module-head'>
        <h2><Video size={18} style={{ verticalAlign: '-3px' }} /> Réunions vidéo</h2>
        <button className='biz-btn-primary sm' onClick={openCreate} data-testid='meeting-add-btn'>
          <Plus size={14} /> Nouvelle réunion
        </button>
      </header>

      <div className='meetings-banner'>
        <Sparkles size={14} />
        <span>
          <b>Gratuit et illimité</b> — propulsé par Jitsi Meet. Vous êtes
          automatiquement <b>l&apos;hôte</b> de chaque salle&nbsp;: muter tout le monde,
          activer le lobby, partager l&apos;écran… Tout est entre vos mains.
        </span>
      </div>

      {loading && <div className='biz-loading'>Chargement…</div>}
      {error && !showForm && <div className='biz-error'>{error}</div>}

      {!loading && items.length === 0 && (
        <div className='biz-empty'>
          Aucune réunion. Cliquez sur « Nouvelle réunion » pour créer votre
          première salle.
        </div>
      )}

      <div className='meetings-grid'>
        {items.map((m) => (
          <article key={m.id} className={`meeting-card status-${m.status}`} data-testid={`meeting-card-${m.id}`}>
            <header className='meeting-card-head'>
              <div className='meeting-card-title'>
                <ShieldCheck size={14} /> {m.title}
              </div>
              <span className={`meeting-status ${m.status}`}>{m.status}</span>
            </header>

            <div className='meeting-card-meta'>
              {m.scheduled_at && (
                <span><CalendarDays size={11} /> {new Date(m.scheduled_at).toLocaleString('fr-FR')}</span>
              )}
              <span>· {m.duration_min} min</span>
              {m.participants?.length > 0 && (
                <span><Users size={11} /> {m.participants.length}</span>
              )}
            </div>

            {m.description && <p className='meeting-card-desc'>{m.description}</p>}

            <div className='meeting-card-link' title={m.join_url}>
              {m.join_url}
            </div>

            <footer className='meeting-card-foot'>
              <button className='biz-btn-primary sm' onClick={() => startMeeting(m)} data-testid={`meeting-start-${m.id}`}>
                <Play size={12} /> Démarrer
              </button>
              <button className='biz-btn-ghost sm' onClick={() => copyInvite(m)}>
                <Copy size={12} /> Copier l&apos;invitation
              </button>
              <button
                className='biz-btn-ghost sm'
                onClick={() => {
                  if (navigator.share) {
                    navigator.share({ title: m.title, text: m.title, url: m.join_url })
                      .catch((err) => console.debug('YNX meetings: navigator.share aborted', err));
                  } else {
                    copyInvite(m);
                  }
                }}
              >
                <Share2 size={12} /> Partager
              </button>
              <button className='biz-icon-btn danger' onClick={() => remove(m.id)} title='Supprimer'>
                <Trash2 size={13} />
              </button>
            </footer>
          </article>
        ))}
      </div>

      {/* ─── CREATE MODAL ─────────────────────────── */}
      {showForm && (
        <div className='biz-modal-overlay' onClick={(e) => e.target === e.currentTarget && setShowForm(false)}>
          <div className='biz-modal' role='dialog' aria-label='Créer une réunion'>
            <header className='biz-modal-head'>
              <h3><Video size={16} style={{ verticalAlign: '-3px' }} /> Nouvelle réunion</h3>
              <button className='biz-icon-btn' onClick={() => setShowForm(false)}><X size={14} /></button>
            </header>
            <div className='biz-modal-body'>
              <label className='biz-label'>Titre *</label>
              <input className='biz-input' value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder='Comité hebdo partenaires' data-testid='meeting-title-input' />

              <label className='biz-label'>Description</label>
              <textarea className='biz-input' rows={2}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder='Ordre du jour, objectifs…' />

              <div className='meeting-form-row'>
                <div>
                  <label className='biz-label'>Date & heure</label>
                  <input className='biz-input' type='datetime-local'
                    value={form.scheduled_at}
                    onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} />
                </div>
                <div>
                  <label className='biz-label'>Durée (min)</label>
                  <input className='biz-input' type='number' min={5} max={600} step={5}
                    value={form.duration_min}
                    onChange={(e) => setForm({ ...form, duration_min: e.target.value })} />
                </div>
              </div>

              <label className='biz-label'>Votre nom (hôte)</label>
              <input className='biz-input' value={form.host_name}
                onChange={(e) => setForm({ ...form, host_name: e.target.value })} />

              <label className='biz-label'>Participants (un par ligne — « Nom &lt;email&gt; »)</label>
              <textarea className='biz-input' rows={4}
                value={form.participants_raw}
                onChange={(e) => setForm({ ...form, participants_raw: e.target.value })}
                placeholder={'Marie Dupont <marie@acme.fr>\npaul@partenaire.com\nJean Martin'} />

              <label className='biz-checkbox'>
                <input type='checkbox' checked={form.require_password}
                  onChange={(e) => setForm({ ...form, require_password: e.target.checked })} />
                Protéger par un mot de passe
              </label>

              {form.require_password && (
                <>
                  <label className='biz-label'>Mot de passe</label>
                  <input className='biz-input' value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    placeholder='Au moins 6 caractères' />
                </>
              )}

              {error && <div className='biz-error'>{error}</div>}
            </div>
            <footer className='biz-modal-foot'>
              <button className='biz-btn-ghost' onClick={() => setShowForm(false)}>Annuler</button>
              <button className='biz-btn-primary' onClick={submit} disabled={busy} data-testid='meeting-save-btn'>
                {busy ? 'Création…' : 'Créer la réunion'}
              </button>
            </footer>
          </div>
        </div>
      )}

      {/* ─── PRE-CHECK MODAL (audio + camera test) ─── */}
      {precheckMeeting && (
        <div className='biz-modal-overlay' onClick={(e) => e.target === e.currentTarget && setPrecheckMeeting(null)}>
          <div className='biz-modal precheck-modal' role='dialog' aria-label='Vérification audio/caméra'>
            <header className='biz-modal-head'>
              <h3><Mic size={16} style={{ verticalAlign: '-3px' }} /> Test audio &amp; caméra</h3>
              <button className='biz-icon-btn' onClick={() => setPrecheckMeeting(null)}><X size={14} /></button>
            </header>
            <div className='biz-modal-body'>
              <p className='precheck-help'>
                Avant de rejoindre <b>{precheckMeeting.title}</b>, vérifiez que
                votre micro capte votre voix (la barre verte doit bouger) et
                que la caméra affiche l&apos;image en direct.
              </p>
              <MediaCheck autoStart />
            </div>
            <footer className='biz-modal-foot'>
              <button className='biz-btn-ghost' onClick={() => setPrecheckMeeting(null)}>Annuler</button>
              <button className='biz-btn-primary' onClick={launchFromPrecheck} data-testid='meeting-launch-btn'>
                <Play size={12} /> Démarrer la réunion
              </button>
            </footer>
          </div>
        </div>
      )}

      {/* ─── ACTIVE JITSI ROOM ───────────────────── */}
      {activeMeeting && (
        <JitsiRoom
          meeting={activeMeeting}
          displayName={currentUser?.name || workspace?.name || 'Hôte YNX'}
          email={currentUser?.email || ''}
          onClose={() => { setActiveMeeting(null); refresh(); }}
        />
      )}
    </div>
  );
};

export default MeetingsModule;
